if (!customElements.get('kit-builder')) {
  customElements.define(
    'kit-builder',
    class KitBuilder extends HTMLElement {
      constructor() {
        super();

        this.sectionId = this.dataset.sectionId;
        this.maxCharms = parseInt(this.dataset.maxCharms) || 3;

        // Parse embedded JSON data
        this.productData = JSON.parse(
          document.getElementById(`KitProductData-${this.sectionId}`).textContent
        );

        const entriesEl = document.getElementById(`KitEntriesData-${this.sectionId}`);
        this.kitEntries = entriesEl ? JSON.parse(entriesEl.textContent) : [];

        const variantsEl = document.getElementById(`KitVariantsData-${this.sectionId}`);
        this.variantsMode = !!variantsEl;
        this.variantsData = variantsEl ? JSON.parse(variantsEl.textContent) : null;

        const addonsEl = document.getElementById(`KitAddonsData-${this.sectionId}`);
        this.addonsData = addonsEl ? JSON.parse(addonsEl.textContent) : [];

        // LocalStorage key scoped to product
        this.storageKey = `kit-builder-${this.productData.id}`;

        // State
        // charms is now slot-indexed: array of length = maxCharms.
        // Each slot is either null (empty) or {variantId, productId, title, variantTitle, price, image}.
        this.state = {
          selectedEntries: [],
          selectedOptions: {},
          charms: new Array(this.maxCharms).fill(null),
        };

        // DOM references
        this.cartItemsEl = this.querySelector('[data-cart-items]');
        this.cartEmptyEl = this.querySelector('[data-cart-empty]');
        this.totalPriceEl = this.querySelector('[data-total-price]');
        this.mobileTotalEl = this.querySelector('[data-mobile-total]');
        this.submitBtns = Array.from(this.querySelectorAll('[data-submit-btn]'));
        // Keep a primary reference for legacy code paths
        this.submitBtn = this.submitBtns[0];
        this.sidebar = this.querySelector(`#KitSidebar-${this.sectionId}`);
        this.drawerOverlay = this.querySelector('[data-drawer-overlay]');
        this.dragHandle = this.querySelector('[data-drag-handle]');
        this.charmsSteps = Array.from(this.querySelectorAll('.kit-builder__addons'));

        this.originalCtaText = this.submitBtn.querySelector('.kit-builder__submit-text').textContent.trim();

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.drawerOpen = false;
        this.mobileMql = window.matchMedia('(max-width: 749px)');
        this.isMobile = this.mobileMql.matches;

        // Mobile: open the first step by default. Desktop: charm steps follow accordion logic
        // (only one open at a time), other steps are always visible.
        if (this.isMobile) {
          const firstStep = this.querySelector('.kit-builder__step');
          if (firstStep) firstStep.classList.add('is-open');
          this.querySelectorAll('.kit-builder__addons').forEach((step) => step.classList.remove('is-open'));
          const firstCharmStep = this.querySelector('.kit-builder__addons');
          if (firstCharmStep) firstCharmStep.classList.add('is-open');
        }

        // React to viewport resize (e.g. orientation change)
        this.mobileMql.addEventListener('change', (ev) => {
          this.isMobile = ev.matches;
          // Switching to desktop: close mobile-only accordions but keep charm-slot accordion
          // behaviour intact (they collapse on desktop too).
          if (!this.isMobile) {
            this.querySelectorAll('.kit-builder__step.is-open').forEach((s) => {
              if (!s.classList.contains('kit-builder__addons')) s.classList.remove('is-open');
            });
          }
        });

        if (this.isMobile) {
          if (this.sidebar) {
            if (this.drawerOverlay) document.body.appendChild(this.drawerOverlay);
            document.body.appendChild(this.sidebar);
          }
        }

        this.bindEvents();
        this.initDrawerDrag();

        // Listen for charm additions coming from either drawer (variant-picker popup or
        // full-product drawer). Both dispatch the same payload shape.
        const handleDrawerAdd = (ev) => {
          const detail = ev.detail || {};
          this.addCharmFromDrawer({
            slot: detail.slot || this.pendingDrawerSlot,
            variantId: detail.variantId,
            productId: detail.productId,
            title: detail.title,
            variantTitle: detail.variantTitle,
            price: detail.price,
            image: detail.image,
          });
        };
        document.addEventListener('kit-charm-drawer:add', handleDrawerAdd);
        document.addEventListener('kit-product-drawer:add', handleDrawerAdd);

        if (this.loadState()) {
          this.renderCart();
        }
        // Always update disabled state so sold-out charms render correctly on first load too.
        this.updateCharmCardsUI();

        // Open the first empty charm slot (on both desktop and mobile) so the user lands
        // on actionable content. If all slots are filled, close everything.
        this.charmsSteps.forEach((s) => s.classList.remove('is-open'));
        const firstEmpty = this.findFirstEmptySlot();
        if (firstEmpty) {
          const target = this.querySelector(`.kit-builder__addons[data-slot="${firstEmpty}"]`);
          if (target) target.classList.add('is-open');
        }

        if (this.isMobile && this.sidebar) {
          setTimeout(() => {
            this.sidebar.classList.add('kit-builder__sidebar--bounce');
            this.sidebar.addEventListener('animationend', () => {
              this.sidebar.classList.remove('kit-builder__sidebar--bounce');
            }, { once: true });
          }, 800);
        }
      }

      bindEvents() {
        const clickHandler = (e) => {
          // Accordion toggle: charm steps are collapsible on all viewports;
          // other steps (kit, options) only collapse on mobile.
          const accordionTrigger = e.target.closest('[data-accordion-trigger]');
          if (accordionTrigger) {
            const step = accordionTrigger.closest('.kit-builder__step');
            const isCharmStep = step && step.classList.contains('kit-builder__addons');
            if (isCharmStep || this.isMobile) {
              // For charm steps: only one open at a time on desktop.
              if (isCharmStep && !this.isMobile) {
                const willOpen = !step.classList.contains('is-open');
                this.charmsSteps.forEach((s) => s.classList.remove('is-open'));
                if (willOpen) step.classList.add('is-open');
              } else {
                this.toggleStep(step);
              }
              return;
            }
          }

          // Charm: peek (eye) icon — opens the product detail drawer
          const peekBtn = e.target.closest('.kit-addon-card__peek');
          if (peekBtn) {
            e.stopPropagation();
            const slot = parseInt(peekBtn.dataset.slot) || this.findFirstEmptySlot() || 1;
            const card = peekBtn.closest('.kit-addon-card');
            const productId = card ? parseInt(card.dataset.productId) : 0;
            this.pendingDrawerSlot = slot;
            document.dispatchEvent(
              new CustomEvent('kit-product-drawer:open', {
                detail: { productUrl: peekBtn.dataset.productUrl, slot, productId },
              })
            );
            return;
          }

          // Charm: remove button (on instances already added) — check BEFORE the add button
          const removeBtn = e.target.closest('.kit-addon-card__remove');
          if (removeBtn) {
            e.stopPropagation();
            const card = removeBtn.closest('.kit-addon-card');
            const slot = parseInt(card.dataset.slot);
            this.clearSlot(slot);
            return;
          }

          // Charm: add button
          const addBtn = e.target.closest('.kit-addon-card__add');
          if (addBtn) {
            e.stopPropagation();
            const card = addBtn.closest('.kit-addon-card');
            this.onAddonAdd(card);
            return;
          }

          // Suggestion charm in fake cart
          const suggestionBtn = e.target.closest('.kit-builder__suggestion-add');
          if (suggestionBtn) {
            e.stopPropagation();
            this.onSuggestionAdd(suggestionBtn);
            return;
          }

          // Kit option card
          const optionCard = e.target.closest('.kit-builder__option-card');
          if (optionCard && !optionCard.disabled) {
            if (this.variantsMode && optionCard.dataset.optionPosition) {
              this.onOptionSelect(optionCard);
            } else {
              this.onEntryToggle(optionCard);
            }
            return;
          }

          // Cart remove (right sidebar)
          const cartRemove = e.target.closest('.kit-builder__cart-remove');
          if (cartRemove) {
            const type = cartRemove.dataset.type;
            if (type === 'entry') {
              this.removeEntry(parseInt(cartRemove.dataset.variantId));
            } else if (type === 'charm-slot') {
              this.clearSlot(parseInt(cartRemove.dataset.slot));
            } else if (type === 'charm') {
              // Legacy uid-based remove (state migrated from previous model)
              this.removeCharmByUid(cartRemove.dataset.uid);
            }
            return;
          }

          // Mobile drawer toggle (via mobile bar button or drag handle)
          const mobileToggle = e.target.closest('.kit-builder__mobile-toggle') ||
            e.target.closest('.kit-builder__drag-handle');
          if (mobileToggle) {
            this.toggleMobileDrawer();
            return;
          }

          const submitBtn = e.target.closest('.kit-builder__submit');
          if (submitBtn && !submitBtn.disabled) {
            this.submitToCart();
            return;
          }
        };

        this.addEventListener('click', clickHandler);
        if (this.isMobile && this.sidebar) {
          this.sidebar.addEventListener('click', clickHandler);
        }

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.sidebar && this.sidebar.classList.contains('is-open')) {
            this.toggleMobileDrawer();
          }
        });
      }

      // =============================================
      // Accordion (mobile only)
      // =============================================

      toggleStep(step) {
        if (!step) return;
        step.classList.toggle('is-open');
      }

      closeStep(step) {
        if (step) step.classList.remove('is-open');
      }

      openStep(step) {
        if (step) step.classList.add('is-open');
      }

      // Close the current step and open the next one (mobile only)
      advanceFromStep(step) {
        if (!this.isMobile || !step) return;
        this.closeStep(step);
        let next = step.nextElementSibling;
        while (next && !next.classList.contains('kit-builder__step')) {
          next = next.nextElementSibling;
        }
        if (next) this.openStep(next);
      }

      // =============================================
      // Kit Entry selection
      // =============================================

      onEntryToggle(card) {
        const variantId = parseInt(card.dataset.variantId);
        const existing = this.state.selectedEntries.find((e) => e.variantId === variantId);

        if (existing) {
          this.removeEntry(variantId);
        } else {
          const entry = this.kitEntries.find((e) => e.variantId === variantId);
          if (!entry) return;

          this.state.selectedEntries = [
            {
              variantId: entry.variantId,
              name: entry.name,
              productTitle: entry.productTitle,
              variantTitle: entry.variantTitle,
              price: entry.price,
              image: entry.image,
            },
          ];

          const group = card.closest('.kit-builder__options');
          if (group) {
            group.querySelectorAll('.kit-builder__option-card.is-selected').forEach((c) => {
              if (c !== card) c.classList.remove('is-selected');
            });
          }
          card.classList.add('is-selected');

          this.renderCart();
        }
      }

      removeEntry(variantId) {
        this.state.selectedEntries = this.state.selectedEntries.filter(
          (e) => e.variantId !== variantId
        );

        if (this.variantsMode) {
          this.state.selectedOptions = {};
          this.querySelectorAll('.kit-builder__option-card.is-selected[data-option-position]').forEach((c) => {
            c.classList.remove('is-selected');
          });
        } else {
          const card = this.querySelector(`.kit-builder__option-card[data-variant-id="${variantId}"]`);
          if (card) card.classList.remove('is-selected');
        }

        this.renderCart();
      }

      // =============================================
      // Variant Option selection (fallback mode)
      // =============================================

      onOptionSelect(card) {
        const position = parseInt(card.dataset.optionPosition);
        const value = card.dataset.optionValue;

        if (this.state.selectedOptions[position] === value) {
          delete this.state.selectedOptions[position];
          card.classList.remove('is-selected');
        } else {
          const container = card.closest('.kit-builder__options');
          container.querySelectorAll('.kit-builder__option-card.is-selected').forEach((c) => {
            c.classList.remove('is-selected');
          });
          this.state.selectedOptions[position] = value;
          card.classList.add('is-selected');
        }

        // Refresh ALL option card images BEFORE resolving — renderCart reads the
        // image straight from each selected card's <img>, so every card needs to
        // reflect the current full selection (in both directions).
        this.refreshOptionImages();
        this.resolveVariant();
      }

      // Updates every option card's thumbnail to match the variant that best
      // represents that card's value combined with the customer's other current
      // selections. This is bidirectional (option1 cards refresh when option2
      // changes too) so the fake cart, which reads back from these images,
      // always reflects the actual selection without needing a re-click.
      refreshOptionImages() {
        if (!this.variantsMode || !this.variantsData) return;
        const data = this.variantsData;

        data.options.forEach((opt) => {
          opt.values.forEach((value) => {
            const card = this.querySelector(
              `.kit-builder__option-card[data-option-position="${opt.position}"][data-option-value="${CSS.escape(value)}"]`
            );
            if (!card) return;

            // Find the variant whose options match this card's value AND every
            // currently-selected option (the exact combination). When admin
            // hasn't assigned a specific photo to that combination, fall back
            // to the product's featured_image rather than picking an unrelated
            // variant — that fallback would otherwise display, e.g., a Beige
            // bracelet for "Argenté" beads when the customer picked Bleu.
            const exact = data.variants.find((v) => {
              if (!v.image) return false;
              if (v.options[opt.position - 1] !== value) return false;
              return data.options.every((o) => {
                if (o.position === opt.position) return true;
                const sel = this.state.selectedOptions[o.position];
                if (!sel) return true;
                return v.options[o.position - 1] === sel;
              });
            });
            const target = exact ? exact.image : (data.productImage || '');
            if (!target) return;

            const img = card.querySelector('.kit-builder__option-image img');
            if (!img) return;
            if (img.getAttribute('src') === target) return;

            // Setting src alone is not enough: image_tag generates srcset+sizes,
            // and the browser will keep showing the srcset-resolved URL until
            // those are cleared.
            img.setAttribute('src', target);
            img.removeAttribute('srcset');
            img.removeAttribute('sizes');
          });
        });
      }

      resolveVariant() {
        const data = this.variantsData;
        const selectedCount = Object.keys(this.state.selectedOptions).length;

        this.state.selectedEntries = [];

        if (selectedCount === data.options.length) {
          const matched = data.variants.find((v) =>
            data.options.every((opt) => {
              const idx = opt.position - 1;
              return v.options[idx] === this.state.selectedOptions[opt.position];
            })
          );

          if (matched && matched.available) {
            this.state.selectedEntries = [
              {
                variantId: matched.id,
                name: this.productData.title,
                productTitle: this.productData.title,
                variantTitle: matched.title,
                price: matched.price,
                image: matched.image || data.productImage || '',
              },
            ];
          }
        }

        this.renderCart();
      }

      // =============================================
      // Charms (slot-indexed: state.charms[i-1] holds the charm for slot i, or null)
      // =============================================

      slotIndex(slot) {
        return slot - 1;
      }

      findFirstEmptySlot() {
        for (let i = 0; i < this.state.charms.length; i++) {
          if (!this.state.charms[i]) return i + 1;
        }
        return null;
      }

      onAddonAdd(card) {
        const slot = parseInt(card.dataset.slot);
        if (!slot) return;

        const productId = parseInt(card.dataset.productId);
        const hasVariants = card.dataset.hasVariants === 'true';
        const product = this.addonsData.find((p) => p.id === productId);
        if (!product) return;

        // Charms with variants: open the popup for variant choice instead of dropping the default.
        if (hasVariants) {
          this.openVariantPopup(product, slot);
          return;
        }

        const defaultVariant = product.variants[0];
        this.setSlotCharm(slot, {
          variantId: defaultVariant.id,
          productId: product.id,
          title: product.title,
          variantTitle: null,
          price: defaultVariant.price,
          image: product.featured_image,
        });
      }

      setSlotCharm(slot, charm) {
        const idx = this.slotIndex(slot);
        if (idx < 0 || idx >= this.state.charms.length) return;
        this.state.charms[idx] = charm;
        this.renderCart();
        this.updateCharmCardsUI();
        this.advanceCharmSlot(slot);
      }

      // After a charm is picked, collapse the current slot's accordion and open the next
      // empty one. If every slot is filled, leave everything closed.
      advanceCharmSlot(currentSlot) {
        this.charmsSteps.forEach((s) => s.classList.remove('is-open'));
        const next = this.findFirstEmptySlot();
        if (next && next !== currentSlot) {
          const target = this.querySelector(`.kit-builder__addons[data-slot="${next}"]`);
          if (target) target.classList.add('is-open');
        }
      }

      clearSlot(slot) {
        const idx = this.slotIndex(slot);
        if (idx < 0 || idx >= this.state.charms.length) return;
        this.state.charms[idx] = null;
        this.renderCart();
        this.updateCharmCardsUI();
      }

      removeCharmByUid(uid) {
        // Backward compat with previous uid-based removal: drop matching slot.
        const idx = this.state.charms.findIndex((c) => c && c.uid === uid);
        if (idx >= 0) this.clearSlot(idx + 1);
      }

      // Public API used by the kit-charm drawer.
      addCharmFromDrawer({ slot, variantId, productId, title, variantTitle, price, image }) {
        const targetSlot = slot || this.findFirstEmptySlot() || 1;
        this.setSlotCharm(targetSlot, {
          variantId,
          productId,
          title,
          variantTitle,
          price,
          image,
        });
      }

      // Adds a charm from the suggestions carousel in the fake cart (no specific slot,
      // fills the first empty slot — falls back to slot 1 if all are full).
      onSuggestionAdd(btn) {
        const productId = parseInt(btn.dataset.productId);
        const product = this.addonsData.find((p) => p.id === productId);
        if (!product) return;

        const targetSlot = this.findFirstEmptySlot();
        if (!targetSlot) return; // all slots filled

        if (!product.has_only_default_variant) {
          this.openVariantPopup(product, targetSlot);
          return;
        }

        const defaultVariant = product.variants[0];
        this.setSlotCharm(targetSlot, {
          variantId: defaultVariant.id,
          productId: product.id,
          title: product.title,
          variantTitle: null,
          price: defaultVariant.price,
          image: product.featured_image,
        });
      }

      // How many slots already use this product's variant (used to cap against inventory).
      countCharmUsageByVariant(variantId) {
        let n = 0;
        this.state.charms.forEach((c) => {
          if (c && c.variantId === variantId) n++;
        });
        return n;
      }

      // True if this addon product can no longer be added (sold out, or inventory cap reached
      // by current slot usage). Excludes the slot in `excludeSlot` from the cap count, so
      // re-adding to a slot already holding that product doesn't count against the cap.
      isAddonExhausted(productId, excludeSlot) {
        const product = this.addonsData.find((p) => p.id === productId);
        if (!product) return true;
        if (product.available === false) return true;

        // Count slot usage for each variant of this product, excluding the given slot.
        const usagePerVariant = new Map();
        this.state.charms.forEach((c, idx) => {
          if (!c) return;
          if (excludeSlot && idx + 1 === excludeSlot) return;
          if (c.productId !== productId) return;
          usagePerVariant.set(c.variantId, (usagePerVariant.get(c.variantId) || 0) + 1);
        });

        // For each variant of the product, check if any could still be added.
        return !product.variants.some((v) => {
          if (!v.available) return false;
          if (v.inventory_management !== 'shopify' || v.inventory_policy === 'continue') {
            // Untracked or backorder allowed: always addable.
            return true;
          }
          const used = usagePerVariant.get(v.id) || 0;
          return used < v.inventory_quantity;
        });
      }

      // Updates the charm cards across all slot blocks:
      // - the card matching the charm in its slot gets `is-added` (shows the "1" badge)
      // - other cards in the same slot are disabled when the addon is exhausted (sold out
      //   or all available units already placed in OTHER slots)
      updateCharmCardsUI() {
        this.querySelectorAll('.kit-addon-card').forEach((card) => {
          const pid = parseInt(card.dataset.productId);
          const slot = parseInt(card.dataset.slot);
          const charm = this.state.charms[this.slotIndex(slot)];
          const isAdded = !!charm && charm.productId === pid;

          card.classList.toggle('is-added', isAdded);

          // Disable when this charm can't be added to this slot because the available stock
          // (counting other slots) is already exhausted. The slot's own card stays clickable
          // so the user can still replace its current pick.
          const exhausted = !isAdded && this.isAddonExhausted(pid, slot);
          card.classList.toggle('is-disabled', exhausted);
          const addBtn = card.querySelector('.kit-addon-card__add');
          if (addBtn) addBtn.disabled = exhausted;
        });

        // Update the per-step status label and `is-filled` class.
        this.querySelectorAll('.kit-builder__addons').forEach((step) => {
          const slot = parseInt(step.dataset.slot);
          const charm = this.state.charms[this.slotIndex(slot)];
          step.classList.toggle('is-filled', !!charm);
          const status = step.querySelector('[data-slot-status]');
          if (status) status.textContent = charm ? charm.title : '';
        });
      }

      generateUid() {
        return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
      }

      // Opens the variant-picker popup — implemented in kit-charm-drawer.js.
      openVariantPopup(product, slot) {
        if (!product) return;
        this.pendingDrawerSlot = slot || this.findFirstEmptySlot() || 1;
        document.dispatchEvent(
          new CustomEvent('kit-charm-drawer:open', {
            detail: { product, slot: this.pendingDrawerSlot },
          })
        );
      }

      // =============================================
      // Fake cart rendering
      // =============================================

      renderCart() {
        const entries = this.state.selectedEntries;
        const filledCharms = this.state.charms.filter(Boolean);
        const hasSelectedOptions = this.variantsMode && Object.keys(this.state.selectedOptions).length > 0;
        const hasItems = entries.length > 0 || filledCharms.length > 0 || hasSelectedOptions;

        if (this.cartEmptyEl) {
          this.cartEmptyEl.style.display = hasItems ? 'none' : '';
        }

        let html = '';

        if (this.variantsMode && Object.keys(this.state.selectedOptions).length > 0) {
          const data = this.variantsData;
          const matched = entries.length > 0 ? entries[0] : null;
          const pricePerOption = matched ? Math.round(matched.price / data.options.length) : 0;
          const priceRemainder = matched ? matched.price - pricePerOption * (data.options.length - 1) : 0;

          html += '<div class="kit-builder__cart-section">';
          html += '<div class="kit-builder__cart-section-label">Kit</div>';

          data.options.forEach((opt, idx) => {
            const value = this.state.selectedOptions[opt.position];
            if (!value) return;

            // Every row shows the EXACT featured_image of the customer's resolved
            // combination (matched.image already falls back to the product image in
            // resolveVariant when the variant has no specific photo). Before the
            // full combination is resolved, pick the variant best matching every
            // selected option for a useful preview.
            let optImage = '';
            if (matched && matched.image) {
              optImage = matched.image;
            } else {
              const partial = data.variants.find((v) => {
                if (!v.image) return false;
                if (v.options[opt.position - 1] !== value) return false;
                return Object.entries(this.state.selectedOptions).every(([pos, val]) => {
                  const posNum = parseInt(pos);
                  if (posNum === opt.position) return true;
                  return v.options[posNum - 1] === val;
                });
              });
              optImage = partial ? partial.image : (data.productImage || '');
            }

            html += this.renderCartItem({
              image: optImage,
              title: opt.name,
              variant: value,
              price: idx === 0 ? priceRemainder : pricePerOption,
              removable: true,
              variantId: matched ? matched.variantId : 0,
              removeType: 'entry',
              hidePrice: true,
            });
          });
          html += '</div>';
        } else if (entries.length > 0) {
          html += '<div class="kit-builder__cart-section">';
          html += '<div class="kit-builder__cart-section-label">Kit</div>';
          entries.forEach((entry) => {
            html += this.renderCartItem({
              image: entry.image,
              title: entry.name || entry.productTitle,
              variant: entry.variantTitle !== 'Default Title' ? entry.variantTitle : null,
              price: entry.price,
              removable: true,
              variantId: entry.variantId,
              removeType: 'entry',
            });
          });
          html += '</div>';
        }

        // Charms section — either show filled slots, or (if entries are picked but no charms yet)
        // a suggestion mini-carousel inviting the customer to add charms.
        if (filledCharms.length > 0) {
          html += '<div class="kit-builder__cart-section">';
          html += '<div class="kit-builder__cart-section-label">Charms</div>';
          this.state.charms.forEach((charm, idx) => {
            if (!charm) return;
            const slot = idx + 1;
            html += this.renderCartItem({
              image: charm.image,
              title: charm.title,
              variant: charm.variantTitle,
              price: charm.price,
              removable: true,
              slot,
              removeType: 'charm-slot',
            });
          });
          html += '</div>';
        } else if (entries.length > 0 && this.addonsData.length > 0) {
          html += this.renderCharmsSuggestions();
        }

        const existingEmpty = this.cartEmptyEl;
        this.cartItemsEl.innerHTML = html;
        if (existingEmpty) {
          this.cartItemsEl.prepend(existingEmpty);
        }

        this.updateTotal();
        const shouldDisable = entries.length === 0;
        this.submitBtns.forEach((b) => (b.disabled = shouldDisable));
        this.saveState();
      }

      renderCharmsSuggestions() {
        if (!this.addonsData || this.addonsData.length === 0) return '';
        // Exclude products with no buyable variant (sold out or zero tracked stock).
        const buyable = this.addonsData.filter((p) => !this.isAddonExhausted(p.id));
        if (buyable.length === 0) return '';
        let html = '<div class="kit-builder__cart-section kit-builder__cart-suggestions">';
        html += '<div class="kit-builder__cart-section-label">Charms</div>';
        html += '<p class="kit-builder__suggestions-hint">Ajoute des charms à ton kit ✨</p>';
        html += '<div class="kit-builder__suggestions-track">';
        buyable.forEach((p) => {
          const imageUrl = typeof p.featured_image === 'string' ? p.featured_image : '';
          html += `
            <div class="kit-builder__suggestion">
              ${imageUrl ? `<div class="kit-builder__suggestion-image"><img src="${imageUrl}" alt="${this.escapeHtml(p.title)}" loading="lazy"></div>` : ''}
              <div class="kit-builder__suggestion-info">
                <div class="kit-builder__suggestion-title">${this.escapeHtml(p.title)}</div>
                <div class="kit-builder__suggestion-price">${this.formatMoney(p.price)}</div>
              </div>
              <button
                type="button"
                class="kit-builder__suggestion-add"
                data-product-id="${p.id}"
                data-product-url="${p.url}"
                data-product-handle="${p.handle}"
                aria-label="Ajouter ${this.escapeHtml(p.title)}"
              >+</button>
            </div>`;
        });
        html += '</div></div>';
        return html;
      }

      renderCartItem({ image, title, variant, price, removable, variantId, uid, slot, removeType, hidePrice }) {
        const imageUrl = image
          ? typeof image === 'string'
            ? image
            : image.src
              ? image.src.replace(/\?.*/, '') + '?width=120'
              : ''
          : '';

        const imageHtml = imageUrl
          ? `<div class="kit-builder__cart-item-image">
              <img src="${imageUrl}" alt="${this.escapeHtml(title)}" width="60" height="60" loading="lazy">
            </div>`
          : '';

        let removeAttrs;
        if (removeType === 'charm-slot') {
          removeAttrs = `data-slot="${slot}" data-type="charm-slot"`;
        } else if (removeType === 'charm') {
          removeAttrs = `data-uid="${uid}" data-type="charm"`;
        } else {
          removeAttrs = `data-variant-id="${variantId}" data-type="entry"`;
        }

        const removeBtn = removable
          ? `<button type="button" class="kit-builder__cart-remove" ${removeAttrs} aria-label="Supprimer">&times;</button>`
          : '';

        return `
          <div class="kit-builder__cart-item">
              ${imageHtml}
              <div class="kit-builder__cart-item-info">
                <div class="kit-builder__cart-item-title">${this.escapeHtml(title)}</div>
                ${variant ? `<div class="kit-builder__cart-item-variant">${this.escapeHtml(variant)}</div>` : ''}
              </div>
              ${hidePrice ? '' : `<div class="kit-builder__cart-item-price">${this.formatMoney(price)}</div>`}
              ${removeBtn}
          </div>
        `;
      }

      updateTotal() {
        let total = 0;
        this.state.selectedEntries.forEach((e) => (total += e.price));
        this.state.charms.forEach((c) => {
          if (c) total += c.price;
        });

        const formatted = this.formatMoney(total);
        this.totalPriceEl.textContent = formatted;
        if (this.mobileTotalEl) {
          this.mobileTotalEl.textContent = formatted;
        }
      }

      // =============================================
      // Cart Submission
      // =============================================

      _setSubmitText(text) {
        this.submitBtns.forEach((b) => {
          const span = b.querySelector('.kit-builder__submit-text');
          if (span) span.textContent = text;
        });
      }

      _setSubmitClass(cls, add) {
        this.submitBtns.forEach((b) => b.classList.toggle(cls, add));
      }

      _setSubmitDisabled(v) {
        this.submitBtns.forEach((b) => (b.disabled = v));
      }

      async submitToCart() {
        if (this.state.selectedEntries.length === 0) return;

        this._setSubmitClass('is-loading', true);
        this._setSubmitDisabled(true);

        const items = [];
        const filledCharms = this.state.charms.filter(Boolean);
        // If charms were picked, generate a kit id so the cart can group kit + charms together.
        const kitId = filledCharms.length > 0
          ? `kit-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`
          : null;

        this.state.selectedEntries.forEach((entry) => {
          const item = { id: entry.variantId, quantity: 1 };
          if (kitId) item.properties = { _kit_id: kitId, _kit_role: 'kit' };
          items.push(item);
        });

        // Aggregate charms by variantId (same charm in two slots → one line with quantity 2).
        const charmCounts = new Map();
        filledCharms.forEach((c) => {
          charmCounts.set(c.variantId, (charmCounts.get(c.variantId) || 0) + 1);
        });
        charmCounts.forEach((qty, variantId) => {
          const item = { id: variantId, quantity: qty };
          if (kitId) item.properties = { _kit_id: kitId, _kit_role: 'charm' };
          items.push(item);
        });

        const sectionsToRender = ['cart-icon-bubble'];

        try {
          const response = await fetch(routes.cart_add_url, {
            ...fetchConfig('javascript'),
            body: JSON.stringify({
              items,
              sections: sectionsToRender,
              sections_url: window.location.pathname,
            }),
          });

          const data = await response.json();

          if (data.status) {
            throw new Error(data.description || data.message);
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'kit-builder',
            cartData: data,
          });

          if (data.sections) {
            this.updateSections(data.sections);
          }
          if (this.cart && this.cart.open) {
            this.cart.open();
          }

          this.state.selectedEntries = [];
          this.state.selectedOptions = {};
          this.state.charms = new Array(this.maxCharms).fill(null);
          this.querySelectorAll('.kit-builder__option-card.is-selected').forEach((c) =>
            c.classList.remove('is-selected')
          );
          this.updateCharmCardsUI();
          this.clearState();

          this._setSubmitClass('is-loading', false);
          this._setSubmitClass('is-success', true);
          this._setSubmitText('Ajouté au panier ✓');

          setTimeout(() => {
            this._setSubmitClass('is-success', false);
            this._setSubmitText(this.originalCtaText);
            this._setSubmitDisabled(false);
          }, 3000);
        } catch (error) {
          console.error('Kit builder: error adding to cart', error);
          this._setSubmitClass('is-loading', false);
          this._setSubmitDisabled(false);
          this._setSubmitText('Erreur — Réessayer');
          setTimeout(() => {
            this._setSubmitText(this.originalCtaText);
          }, 3000);
        }
      }

      updateSections(sections) {
        Object.entries(sections).forEach(([sectionId, html]) => {
          const el = document.getElementById(sectionId);
          if (el) {
            el.innerHTML = new DOMParser()
              .parseFromString(html, 'text/html')
              .querySelector('.shopify-section')?.innerHTML || html;
          }
        });
      }

      // =============================================
      // Mobile drawer (unchanged)
      // =============================================

      toggleMobileDrawer() {
        if (!this.sidebar) return;
        if (this.drawerOpen) this.closeDrawer();
        else this.openDrawer();
      }

      openDrawer() {
        this.drawerOpen = true;
        this.sidebar.classList.add('is-open');
        if (this.drawerOverlay) this.drawerOverlay.classList.add('is-visible');
        document.body.style.overflow = 'hidden';
      }

      closeDrawer() {
        this.drawerOpen = false;
        this.sidebar.classList.remove('is-open');
        this.sidebar.style.transform = '';
        if (this.drawerOverlay) {
          this.drawerOverlay.classList.remove('is-visible');
          this.drawerOverlay.style.opacity = '';
        }
        document.body.style.overflow = '';
      }

      initDrawerDrag() {
        if (!this.sidebar) return;

        const dragAreas = [this.sidebar];
        let startY = 0;
        let currentY = 0;
        let startTime = 0;
        let isDragging = false;
        let dragLocked = false;
        const collapsedOffset = this.sidebar.offsetHeight ? this.sidebar.offsetHeight - 96 : 400;
        const cartContent = this.sidebar.querySelector('.kit-builder__cart');

        const onTouchStart = (e) => {
          startY = e.touches[0].clientY;
          currentY = startY;
          startTime = Date.now();
          isDragging = true;
          dragLocked = false;
          this._touchTarget = e.target;
        };

        const onTouchMove = (e) => {
          if (!isDragging) return;
          currentY = e.touches[0].clientY;
          const deltaY = currentY - startY;

          if (!dragLocked && Math.abs(deltaY) > 8) {
            if (!this.drawerOpen) dragLocked = true;
            else if (deltaY > 0 && cartContent && cartContent.scrollTop <= 0) dragLocked = true;
            else { isDragging = false; return; }
            this.sidebar.classList.add('is-dragging');
          }

          if (!dragLocked) return;

          if (this.drawerOpen) {
            const clamped = Math.max(0, deltaY);
            this.sidebar.style.transform = `translateY(${clamped}px)`;
            if (this.drawerOverlay) {
              const progress = Math.min(clamped / collapsedOffset, 1);
              this.drawerOverlay.style.opacity = 1 - progress;
            }
          } else {
            const clamped = Math.min(0, deltaY);
            this.sidebar.style.transform = `translateY(calc(100% - 96px + ${clamped}px))`;
            if (this.drawerOverlay) {
              const progress = Math.min(Math.abs(clamped) / collapsedOffset, 1);
              this.drawerOverlay.classList.add('is-visible');
              this.drawerOverlay.style.opacity = progress;
            }
          }
        };

        const onTouchEnd = () => {
          if (!isDragging && !dragLocked) return;
          const wasDragging = dragLocked;
          isDragging = false;
          dragLocked = false;
          this.sidebar.classList.remove('is-dragging');
          this.sidebar.style.transform = '';

          const deltaY = currentY - startY;
          const elapsed = Date.now() - startTime;

          if (elapsed < 300 && Math.abs(deltaY) < 10) {
            const tappedBar = this._touchTarget && (
              this._touchTarget.closest('.kit-builder__mobile-bar') ||
              this._touchTarget.closest('.kit-builder__drag-handle')
            );
            if (tappedBar) this.toggleMobileDrawer();
            return;
          }

          if (!wasDragging) return;

          const velocity = Math.abs(deltaY) / elapsed;
          const threshold = collapsedOffset * 0.3;
          const isFlick = velocity > 0.5;

          if (this.drawerOpen) {
            if ((isFlick && deltaY > 0) || deltaY > threshold) this.closeDrawer();
            else {
              this.sidebar.style.transform = '';
              if (this.drawerOverlay) this.drawerOverlay.style.opacity = '';
            }
          } else {
            if ((isFlick && deltaY < 0) || Math.abs(deltaY) > threshold) this.openDrawer();
            else {
              this.sidebar.style.transform = '';
              if (this.drawerOverlay) {
                this.drawerOverlay.classList.remove('is-visible');
                this.drawerOverlay.style.opacity = '';
              }
            }
          }
        };

        dragAreas.forEach((area) => {
          area.addEventListener('touchstart', onTouchStart, { passive: true });
          area.addEventListener('touchmove', onTouchMove, { passive: true });
          area.addEventListener('touchend', onTouchEnd, { passive: true });
        });

        if (this.drawerOverlay) {
          this.drawerOverlay.addEventListener('click', () => this.closeDrawer());
        }
      }

      // =============================================
      // LocalStorage persistence
      // =============================================

      saveState() {
        try {
          const data = {
            selectedEntries: this.state.selectedEntries,
            selectedOptions: this.state.selectedOptions,
            charms: this.state.charms,
          };
          localStorage.setItem(this.storageKey, JSON.stringify(data));
        } catch (e) { /* ignore */ }
      }

      loadState() {
        try {
          const raw = localStorage.getItem(this.storageKey);
          if (!raw) return false;
          const data = JSON.parse(raw);

          if (data.selectedEntries && Array.isArray(data.selectedEntries)) {
            this.state.selectedEntries = data.selectedEntries;
            if (!this.variantsMode) {
              data.selectedEntries.forEach((entry) => {
                const card = this.querySelector(`.kit-builder__option-card[data-variant-id="${entry.variantId}"]`);
                if (card) card.classList.add('is-selected');
              });
            }
          }

          if (data.selectedOptions && typeof data.selectedOptions === 'object') {
            this.state.selectedOptions = data.selectedOptions;
            if (this.variantsMode) {
              Object.entries(data.selectedOptions).forEach(([position, value]) => {
                const card = this.querySelector(
                  `.kit-builder__option-card[data-option-position="${position}"][data-option-value="${CSS.escape(value)}"]`
                );
                if (card) card.classList.add('is-selected');
              });
              this.refreshOptionImages();
              this.resolveVariant();
            }
          }

          // Always work with a fixed-length, slot-indexed array (length = maxCharms).
          const slots = new Array(this.maxCharms).fill(null);

          if (data.charms && Array.isArray(data.charms)) {
            // Detect migration from the previous "ordered list" model (no slot info,
            // just push entries onto consecutive slots).
            data.charms.slice(0, this.maxCharms).forEach((c, i) => {
              if (c) slots[i] = { ...c };
            });
          } else if (data.charmSlots && Array.isArray(data.charmSlots)) {
            data.charmSlots.slice(0, this.maxCharms).forEach((c, i) => {
              if (c) slots[i] = { ...c };
            });
          } else if (data.addons && Array.isArray(data.addons)) {
            data.addons.slice(0, this.maxCharms).forEach((c, i) => {
              if (c) slots[i] = { ...c };
            });
          }

          this.state.charms = slots;

          return true;
        } catch (e) {
          return false;
        }
      }

      clearState() {
        try { localStorage.removeItem(this.storageKey); } catch (e) { /* ignore */ }
      }

      // =============================================
      // Utilities
      // =============================================

      formatMoney(cents) {
        const amount = (cents / 100).toFixed(2).replace('.', ',');
        const symbol = this.productData.currency || '€';
        return `${amount}\u00A0${symbol}`;
      }

      escapeHtml(str) {
        if (!str) return '';
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
      }
    }
  );
}

if (!customElements.get('inspiration-carousel')) {
  customElements.define(
    'inspiration-carousel',
    class InspirationCarousel extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('[data-viewport]');
        this.track = this.querySelector('[data-track]');
        this.slides = Array.from(this.querySelectorAll('[data-slide]'));
        this.prevBtn = this.querySelector('[data-prev]');
        this.nextBtn = this.querySelector('[data-next]');
        this.loop = this.dataset.loop === 'true';
        this.index = 0;

        if (!this.viewport || !this.track || this.slides.length === 0) return;

        this.prevBtn?.addEventListener('click', (e) => { e.preventDefault(); this.prev(); });
        this.nextBtn?.addEventListener('click', (e) => { e.preventDefault(); this.next(); });

        this.bindPointer();

        this._onResize = () => this.update();
        window.addEventListener('resize', this._onResize);

        // Recalc once images load — slide offsetLeft can shift while images
        // resolve their intrinsic size, even with aspect-ratio reserved.
        this.querySelectorAll('img').forEach((img) => {
          if (!img.complete) img.addEventListener('load', this._onResize, { once: true });
        });

        this.update();
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this._onResize);
      }

      // Stride = distance from one slide's left edge to the next, gap included.
      stride() {
        if (this.slides.length < 2) return this.slides[0]?.offsetWidth || 0;
        return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
      }

      visibleCount() {
        const s = this.stride();
        if (!s) return 1;
        return Math.max(1, Math.round(this.viewport.clientWidth / s));
      }

      maxIndex() {
        return Math.max(0, this.slides.length - this.visibleCount());
      }

      go(index) {
        const max = this.maxIndex();
        if (this.loop) {
          if (index < 0) index = max;
          else if (index > max) index = 0;
        } else {
          index = Math.max(0, Math.min(max, index));
        }
        this.index = index;
        this.update();
      }

      prev() { this.go(this.index - 1); }
      next() { this.go(this.index + 1); }

      update() {
        if (this.index > this.maxIndex()) this.index = this.maxIndex();
        const offset = this.index * this.stride();
        this.track.style.transform = `translateX(${-offset}px)`;
      }

      // Pointer drag: tracks raw movement on the track, then snaps to nearest
      // index on release. Threshold = 15% of the viewport width.
      bindPointer() {
        let startX = 0;
        let startOffset = 0;
        let dragging = false;
        let pointerId = null;

        const onDown = (e) => {
          if (e.button !== undefined && e.button !== 0) return;
          // Ignore drags that start on the arrow buttons.
          if (e.target.closest('[data-prev], [data-next]')) return;
          dragging = true;
          pointerId = e.pointerId;
          startX = e.clientX;
          startOffset = -this.index * this.stride();
          this.track.classList.add('is-dragging');
          try { this.viewport.setPointerCapture(pointerId); } catch (_) { /* ignore */ }
        };

        const onMove = (e) => {
          if (!dragging) return;
          const dx = e.clientX - startX;
          this.track.style.transform = `translateX(${startOffset + dx}px)`;
        };

        const onUp = (e) => {
          if (!dragging) return;
          dragging = false;
          this.track.classList.remove('is-dragging');
          try { this.viewport.releasePointerCapture(pointerId); } catch (_) { /* ignore */ }
          const dx = e.clientX - startX;
          const threshold = Math.max(40, this.viewport.clientWidth * 0.15);
          if (dx <= -threshold) this.next();
          else if (dx >= threshold) this.prev();
          else this.update();
        };

        this.viewport.addEventListener('pointerdown', onDown);
        this.viewport.addEventListener('pointermove', onMove);
        this.viewport.addEventListener('pointerup', onUp);
        this.viewport.addEventListener('pointercancel', onUp);

        // Suppress click on child links if the pointer actually moved (drag, not tap).
        this.viewport.addEventListener('click', (e) => {
          if (this.track.classList.contains('is-dragging')) return;
        }, true);
      }
    }
  );
}
